import dayjs from 'dayjs';
import {NumberInfo} from '@di-nxt/components';
import {useCallback, useMemo, useState} from 'react';
import {
  useMemberRequestsOperations,
  useSnacksActions,
  useTeam,
  uuidv4,
} from '../../../../services';
import {getNonemptyMembers, validateMembers} from '../functions';
import {NewMember} from '../types';
import {t} from '../../../../localization';

const initialMemberValue: NewMember = {
  numberInfo: undefined,
  startDate: undefined,
};

const updateMember = (
  updatedMember: Partial<NewMember>,
  index: number,
  members: NewMember[],
): NewMember[] => {
  const updatedMembers = [...members];
  const oldMember = updatedMembers[index];
  updatedMembers[index] = {
    ...oldMember,
    ...updatedMember,
  };

  return updatedMembers;
};

export const useNewMembers = (
  teamId: number,
  callback: (success: boolean) => void,
) => {
  const team = useTeam(teamId);

  // Members
  const [newMembers, setNewMembers] = useState<NewMember[]>(() => [
    initialMemberValue,
  ]);

  // Snack
  const {add: addSnack} = useSnacksActions();

  // Operations
  const {create: createMemberRequest} = useMemberRequestsOperations();

  // Handle number and enabled/disabled
  const isValid = useMemo(() => validateMembers(newMembers), [newMembers]);

  // Callbacks for manipulating data
  const handlePhoneChanged = useCallback(
    (index: number, numberInfo: NumberInfo) =>
      setNewMembers(oldMembers =>
        updateMember({numberInfo}, index, oldMembers),
      ),
    [],
  );

  const handleStartDateChanged = useCallback(
    (index: number, startDate: Date) =>
      setNewMembers(oldMembers =>
        updateMember(
          {
            startDate: startDate,
          },
          index,
          oldMembers,
        ),
      ),
    [],
  );

  // Submit/cancel
  const handleCancel = useCallback(() => callback(false), [callback]);

  const handleSubmit = useCallback(() => {
    (getNonemptyMembers(newMembers) as NewMember[]).forEach(m => {
      if (!team) {
        throw Error('Missing team when adding people');
      }
      createMemberRequest({
        teamId: team.id,
        mobileNo: m.numberInfo!.number!,
        employmentStart: dayjs(m.startDate).format('YYYY-MM-DD'),
        id: uuidv4(),
      });
    });
    addSnack(t('screens.members.snacks.invitationsent'));
    setNewMembers([initialMemberValue]);
    callback(true);
  }, [newMembers, addSnack, callback, team, createMemberRequest]);

  const handleAddRow = useCallback(() => {
    setNewMembers(val => [...val, initialMemberValue]);
  }, []);

  return {
    team,
    isValid,
    newMembers,
    handleAddRow,
    handleCancel,
    handleSubmit,
    handlePhoneChanged,
    handleStartDateChanged,
  };
};
